import type { InvestmentsReportTemplate } from "@eu.mdotm.api/sphere";
import type { TemplateSummary } from "@eu.mdotm.api/storyfolio-gateway";
import type { IconName } from "@mdotm/shadcn/legacy/components";

// The one list the export menus read, across the two engines that can fill it: the Report Builder
// (the monolith's PDF/XLSX templates, including the built-in defaults) and Storyfolio V2
// (the Storyfolio gateway's catalogue). Entries are merged and presented alike — a user picks a
// template, not an engine.

/** A Report Builder template: the monolith's own, and the built-in defaults carrying an `id`. */
export type ReportBuilderTemplate = InvestmentsReportTemplate & { id?: string; engine?: never };

/**
 * A Storyfolio V2 template, adapted to what the export menus read.
 *
 * Carries `templateName` and `visible` so that a menu filtering and labelling the list needs no
 * branch for it, and `templateId`, the gateway's opaque key that generation selects by. The listing
 * holds one entry per template, at the current version `templateVersion` records.
 */
export type StoryfolioV2Template = {
	engine: "storyfolio-v2";
	templateId: string;
	templateVersion: number;
	templateName: string;
	visible: true;
	format: "pptx";
	description?: string;
	defaultLanguage: string;
	supportedLanguages: string[];
};

export type ExportTemplate = ReportBuilderTemplate | StoryfolioV2Template;

/**
 * Which engine generates this template.
 *
 * Test this BEFORE `isDefaultReportTemplate`, which reads `!("uuid" in obj)`: a Storyfolio V2
 * template has no uuid either, so an unguarded default-template check claims it and then reads an
 * `id` it does not have.
 */
export function isStoryfolioV2Template(template: ExportTemplate): template is StoryfolioV2Template {
	return template.engine === "storyfolio-v2";
}

/**
 * The gateway's listing entry as the export menus consume it.
 *
 * `format` is fixed because the gateway emits one: `BulkDocumentRequest.format` and
 * `GenerateArtifactRequest.format` both enumerate `pptx` alone, and `TemplateSummary` carries no
 * format of its own to read. It becomes a real field the day the contract grows a second one.
 */
export function toStoryfolioV2Template(summary: TemplateSummary): StoryfolioV2Template {
	return {
		engine: "storyfolio-v2",
		templateId: summary.templateId,
		templateVersion: summary.templateVersion,
		templateName: summary.name,
		visible: true,
		format: "pptx",
		...(summary.description ? { description: summary.description } : {}),
		defaultLanguage: summary.defaultLanguage,
		supportedLanguages: summary.supportedLanguages,
	};
}

/** The output format an entry produces, lowercased. `pdf` where a Report Builder entry names none. */
export function exportTemplateFormat(template: ExportTemplate): string {
	return (template.format ?? "pdf").toLowerCase();
}

/**
 * The label an entry carries in the menu, format included.
 *
 * Every entry is suffixed, not only Storyfolio V2's. A suffix on one engine's entries alone
 * would be the visual distinction the product document rules out — and a user picking a PowerPoint
 * template has to know before the click, not after it.
 */
export function exportTemplateLabel(template: ExportTemplate): string {
	// TODO(MDOT-25287): Storyfolio V2 names are unique only per owner, so a COMMON template and a customer's own
	// one can share this label. The bulk menus' qualifier `…/Report(${templateName})` then collides, as it already
	// does for a Report Builder and a Storyfolio V2 template of the same name, whose labels differ only by format.
	// How should the menu tell same-named templates apart, and what should the qualifier key on?
	return `${template.templateName ?? ""} (${exportTemplateFormat(template)})`;
}

/** The menu icon for an entry's output format. */
export function exportTemplateIcon(template: ExportTemplate): IconName {
	switch (exportTemplateFormat(template)) {
		case "pptx":
			return "ppt";
		case "xlsx":
			return "xls";
		default:
			return "pdf";
	}
}

/**
 * A stable identifier for an entry, for analytics.
 *
 * A Storyfolio V2 entry is identified by the gateway's `templateId`, which holds across versions:
 * the listing carries one entry per template, at its current version.
 */
export function exportTemplateId(template: ExportTemplate): string {
	if (isStoryfolioV2Template(template)) {
		return template.templateId;
	}
	return template.uuid ?? template.id ?? "";
}
